"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { mutation, query } from "@core/helpers/graphql";
import { minimize_str } from "@core/helpers/string";

/**
 * ورود یکپارچه (SSO) به پنل فروشندگان.
 *
 * صفحه‌ی ورود پنل یک آدرس ثابت است؛ کد یک‌بارمصرف به شکل کوئری‌پارامتر `sso`
 * به آن داده می‌شود و پنل بعد از خواندن، کاربر را لاگین می‌کند و پارامتر را از
 * آدرس پاک می‌کند.
 */
export const PANEL_LOGIN_URL = "https://seller.jument.style/login";

/**
 * فقط `can_access_panel` خوانده می‌شود، چون بقیه‌ی فیلدهای کاربر را
 * AuthContext از قبل دارد. این فیلد همیشه مربوط به خود کاربر درخواست‌کننده
 * است و روی کاربران دیگر (مثل کاربر یک سفارش) همیشه false برمی‌گردد، پس با آن
 * نمی‌شود کسی را «فروشنده» برچسب زد.
 */
const GET_PANEL_ACCESS = minimize_str(`
  query {
    result: getCurrentUser {
      id
      can_access_panel
    }
  }
`);

/**
 * تیکت (کد یک‌بارمصرف) ورود به پنل.
 *
 * از `expires_at` و `site_id` صرف‌نظر شده است: بلافاصله بعد از گرفتن کد ریدایرکت
 * می‌کنیم، پس عمر ۶۰ ثانیه‌ای کد جایی محاسبه نمی‌شود و پنل هم برای شناسایی سایت
 * نیازی به این مقدار از سمت ما ندارد.
 *
 * هر کاربر در هر لحظه فقط یک تیکت دارد؛ گرفتن تیکت دوم تیکت قبلی را بی‌سر‌و‌صدا
 * باطل می‌کند. به همین دلیل این موتیشن فقط با کلیک کاربر صدا زده می‌شود و هرگز
 * موقع رندر شدن دکمه، وگرنه دو تب باز، تیکت همدیگر را خراب می‌کنند.
 */
const CREATE_PANEL_LOGIN_TICKET = minimize_str(`
  mutation {
    result: createPanelLoginTicket {
      code
    }
  }
`);

/**
 * نتیجه‌ی بررسی دسترسی در سطح ماژول کش می‌شود تا چند مصرف‌کننده (منوی کاربر در
 * هدر و تب‌های صفحه‌ی حساب کاربری) با هم فقط یک درخواست بزنند.
 */
let access_promise = null;

const fetch_panel_access = () => {
	if (!access_promise) {
		// query() هسته reject نمی‌کند؛ خطا همان `status: false` است.
		access_promise = query(GET_PANEL_ACCESS).then((result) =>
			// کاربر مهمان: getCurrentUser مقدار null دارد.
			result?.status ? !!result?.data?.can_access_panel : false,
		);
	}
	return access_promise;
};

/**
 * آدرس مقصد نه لاگ می‌شود و نه جایی ذخیره؛ کد داخل کوئری‌استرینگ است و فقط به
 * این دلیل قابل قبول است که ۶۰ ثانیه و یک بار بیشتر کار نمی‌کند. با
 * `location.replace` می‌رویم تا در تاریخچه‌ی مرورگر هم نماند.
 */
const go_to_panel = (target_window, url) => {
	if (target_window) {
		target_window.location.replace(url);
		return;
	}
	// تب جدید توسط مرورگر بلاک شده است؛ کد سوخته نشود.
	window.location.replace(url);
};

/**
 * @returns {{ can_access_panel: boolean, open_panel: (event?: object) => Promise<boolean>, opening_panel: boolean }}
 */
const useSellerPanel = () => {
	const { is_auth } = useAuthContext();
	const [can_access_panel, set_can_access_panel] = useState(false);
	const [opening_panel, set_opening_panel] = useState(false);

	useEffect(() => {
		if (!is_auth) {
			// خروج از حساب: کش باید برای کاربر بعدی خالی باشد.
			access_promise = null;
			set_can_access_panel(false);
			return;
		}

		let is_mounted = true;
		fetch_panel_access().then((has_access) => {
			if (is_mounted) set_can_access_panel(has_access);
		});

		return () => {
			is_mounted = false;
		};
	}, [is_auth]);

	const open_panel = useCallback(
		async (event) => {
			// کلیک با ctrl/cmd/shift یا دکمه‌ی وسط: مرورگر خودش تب باز می‌کند و
			// کاربر صفحه‌ی ورود عادی پنل را می‌بیند. تیکتی مصرف نمی‌کنیم.
			if (
				event?.metaKey ||
				event?.ctrlKey ||
				event?.shiftKey ||
				event?.altKey ||
				(typeof event?.button === "number" && event.button !== 0)
			) {
				return false;
			}

			event?.preventDefault?.();
			if (opening_panel) return false;
			set_opening_panel(true);

			// تب باید همین‌جا و به صورت سینک باز شود؛ اگر بعد از await باز شود
			// بلاک‌کننده‌ی پاپ‌آپ آن را می‌بندد چون دیگر داخل کلیک کاربر نیست.
			let panel_tab = null;
			try {
				panel_tab = window.open("about:blank", "_blank");
				if (panel_tab) panel_tab.opener = null;
			} catch {
				panel_tab = null;
			}

			try {
				const result = await mutation(CREATE_PANEL_LOGIN_TICKET, undefined, {
					// کد یک‌بارمصرف در کش اپولو نوشته نشود.
					fetchPolicy: "no-cache",
				});
				const code = result?.status ? result?.data?.code : null;

				if (!code) {
					// تیکت گرفته نشد (مثلا محدودیت نرخ روی IP)؛ کاربر را با
					// صفحه‌ی ورود عادی تنها نگذاریم تا دستی وارد شود.
					// حالت «دسترسی ندارد» اینجا نمی‌افتد، چون لینک از قبل با
					// can_access_panel فیلتر شده است. اگر خطا واقعا 401 باشد
					// (توکن منقضی) هلپر مشترک خودش کاربر را از سایت خارج
					// می‌کند و همین رفتار درست است.
					go_to_panel(panel_tab, PANEL_LOGIN_URL);
					return false;
				}

				go_to_panel(
					panel_tab,
					`${PANEL_LOGIN_URL}?sso=${encodeURIComponent(code)}`,
				);
				return true;
			} finally {
				set_opening_panel(false);
			}
		},
		[opening_panel],
	);

	return { can_access_panel, open_panel, opening_panel };
};

export default useSellerPanel;
